/**
 * InstanceControlSection — "Dagens oppgaver", the live-instance list.
 *
 * The spawner is a cron and it does not ask. It will produce a card bound to
 * the wrong kid, or one nobody should do today, and until this panel existed a
 * parent's only options were to wait for it to expire or edit the template
 * (which does not touch already-spawned instances — CHORE-04 snapshots).
 *
 * Three actions per row:
 *   Flytt        reassign to another member; the card returns to the pool
 *                unclaimed, because claiming is the kid's act.
 *   Avlys        terminal cancel — its own status, not 'expired'.
 *   Hent tilbake restore a cancelled or expired card to the pool.
 *
 * Mutations run through the same runGuarded + <AdminReauthModal> recovery the
 * template library uses, so a 403 admin-scope error re-prompts and retries
 * rather than dropping the parent's action on the floor.
 *
 * #159 (redesign steg 9b, Stell_Maler): kortet «Oppgaver i dag» øverst på
 * Oppgavemaler har fire tellefliser — ledige, tatt, venter, utløpt. En flis
 * folder ut listen under, filtrert på den statusen; samme flis igjen folder den
 * sammen. Tallet på flisen er lengden på den samme spørringen listen viser,
 * så de to sier aldri noe forskjellig. Alt her er dagens kort (Oslo-dagen,
 * `?date=` i useLiveInstances), uten tak. Statuschipsene i listen er der fortsatt,
 * så Alle og Avlyst er ett trykk unna.
 *
 * Navnene kommer fra medlemslisten, som krever adminopplåsing. Er den ute,
 * vet vi ikke hvem som tok kortet; da sier raden «Tatt» eller «Tildelt» uten
 * navn i stedet for «Åpen for alle», som ville vært feil.
 */
import { useId, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button, Chip, LoadingState, ErrorState } from '../../components/primitives';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import { AdminReauthModal } from '../admin/AdminReauthModal';
import { ApiError, isAdminScopeError, useAdminMembers } from '../admin/useMembers';
import {
  useCancelInstance,
  useLiveInstances,
  useReassignInstance,
  useRestoreInstance,
  type InstanceFilter,
  type InstanceRow,
} from './useInstanceControl';

const FILTERS: readonly InstanceFilter[] = [
  'all',
  'available',
  'claimed',
  'awaiting_approval',
  'cancelled',
  'expired',
] as const;

/** Statuses a parent can still act on with Flytt / Avlys. */
const LIVE = new Set(['available', 'claimed', 'awaiting_approval']);
/** Statuses Hent tilbake applies to. */
const RESTORABLE = new Set(['cancelled', 'expired']);

/** Flisene i «Oppgaver i dag», i skissens rekkefølge og farger. */
const TILES: ReadonlyArray<{ status: InstanceFilter; tone: string; label: string }> = [
  { status: 'available', tone: 'bg-inset text-ink', label: 'text-ink-2' },
  { status: 'claimed', tone: 'bg-blue-bg text-blue', label: '' },
  { status: 'awaiting_approval', tone: 'bg-warn-bg text-warn', label: '' },
  { status: 'expired', tone: 'bg-inset text-ink-2', label: '' },
];

const STATUS_TONE: Record<string, string> = {
  available: 'bg-ok-bg text-ink',
  // accent-text, ikke primary: dyp grønn på lime-tonen er 4.3:1 i lyst tema (#151).
  claimed: 'bg-lime-bg text-accent-text',
  // Samme warn-par som StatusPill «venter» — fylt amber + ink holder ikke i mørkt tema (#151).
  awaiting_approval: 'bg-warn-bg text-warn',
  completed: 'bg-ok-bg text-ink',
  rejected: 'bg-inset text-ink-2',
  expired: 'bg-inset text-ink-2',
  cancelled: 'bg-inset text-ink-2',
};

export function InstanceControlSection(): ReactElement {
  const { t } = useTranslation();
  // Starter på «ledige», ikke «alle»: mens listen er foldet sammen, er det da
  // samme spørring som flisen, og ingen ekstra henting som ingen ser.
  const [filter, setFilter] = useState<InstanceFilter>('available');
  const [open, setOpen] = useState(false);
  const panelId = useId();
  // Én spørring per flis (hooks i fast rekkefølge). Det er de samme nøklene
  // listen bruker når flisen er valgt, så et trykk viser det som ble talt.
  const tileCounts = [
    useLiveInstances('available'),
    useLiveInstances('claimed'),
    useLiveInstances('awaiting_approval'),
    useLiveInstances('expired'),
  ];
  const list = useLiveInstances(filter);
  const members = useAdminMembers();
  const reassignMut = useReassignInstance();
  const cancelMut = useCancelInstance();
  const restoreMut = useRestoreInstance();

  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<InstanceRow | null>(null);
  const [reassignFor, setReassignFor] = useState<string | null>(null);
  const [reauth, setReauth] = useState<{ open: boolean; retry: (() => void) | null }>({
    open: false,
    retry: null,
  });

  const rows = list.data ?? [];
  const kids = (members.data ?? []).filter((m) => m.role !== 'admin');

  async function runGuarded(fn: () => Promise<void>): Promise<void> {
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      if (isAdminScopeError(err)) {
        setReauth({ open: true, retry: () => void runGuarded(fn) });
        return;
      }
      setActionError(err instanceof ApiError ? err.code : 'unknown');
    }
  }

  function onReauthSuccess(): void {
    const retry = reauth.retry;
    setReauth({ open: false, retry: null });
    retry?.();
  }

  function nameOf(id: string | null): string | null {
    if (!id) return null;
    return (members.data ?? []).find((m) => m.id === id)?.short_name ?? null;
  }

  /** Who this card is for, in one phrase. */
  function whoFor(row: InstanceRow): string {
    if (row.claimed_by) {
      const claimer = nameOf(row.claimed_by);
      return claimer ? t('admin.instances.takenBy', { name: claimer }) : t('admin.instances.taken');
    }
    if (row.assigned_to) {
      const assignee = nameOf(row.assigned_to);
      return assignee
        ? t('admin.instances.assignedTo', { name: assignee })
        : t('admin.instances.assigned');
    }
    return t('admin.instances.unassigned');
  }

  /** En flis folder ut listen på sin status, eller sammen igjen om den alt er valgt. */
  function onTile(status: InstanceFilter): void {
    if (open && filter === status) {
      setOpen(false);
      // Tilbake til flisens spørring, så den foldede listen ikke henter et
      // chipfilter ingen ser.
      setFilter('available');
      return;
    }
    setFilter(status);
    setOpen(true);
  }

  return (
    <section
      aria-labelledby={`${panelId}-title`}
      className={`${GLASS_SURFACE} flex flex-col gap-3 rounded-lg px-3.5 py-3 shadow-md`}
      data-testid="instance-control-section"
    >
      <div className="flex items-baseline justify-between gap-2">
        <h2 id={`${panelId}-title`} className="text-sm font-extrabold text-ink">
          {t('admin.instances.section_label')}
        </h2>
        <span className="text-caption font-bold text-ink-2">{t('admin.instances.tapHint')}</span>
      </div>

      <div className="grid grid-cols-4 gap-1.5 text-center">
        {TILES.map(({ status, tone, label }, i) => {
          const q = tileCounts[i]!;
          const current = open && filter === status;
          return (
            <button
              key={status}
              type="button"
              aria-expanded={current}
              aria-controls={panelId}
              data-testid={`instance-tile-${status}`}
              onClick={() => onTile(status)}
              className={[
                'flex min-h-14 flex-col items-center justify-center rounded-md px-1 py-2',
                tone,
                current ? 'ring-2 ring-primary' : '',
              ].join(' ')}
            >
              <span className="tnum text-lg font-extrabold">{q.data ? q.data.length : '–'}</span>
              <span className={`text-label font-bold ${label}`}>
                {t(`admin.instances.tile.${status}`)}
              </span>
            </button>
          );
        })}
      </div>

      {/* Utenfor panelet: en handling kan feile etter at kortet er foldet sammen. */}
      {actionError ? (
        <p className="text-sm text-bad" data-testid="instance-action-error">
          {t('admin.instances.actionError')}
        </p>
      ) : null}

      {open ? (
        <div id={panelId} className="flex flex-col gap-3 border-t border-card-line pt-3">
          <div
            role="group"
            aria-label={t('admin.instances.filterLabel')}
            data-testid="instance-filter"
            className="flex flex-wrap gap-1.5"
          >
            {FILTERS.map((f) => (
              <Chip
                key={f}
                selected={filter === f}
                data-testid={`instance-filter-${f}`}
                onClick={() => setFilter(f)}
              >
                {t(`admin.instances.filter.${f}`)}
              </Chip>
            ))}
          </div>

          <p className="text-caption text-ink-2" data-testid="instance-count">
            {t('admin.instances.count', { count: rows.length })}
          </p>

          {list.isLoading ? (
            <LoadingState lines={3} />
          ) : list.isError ? (
            <ErrorState
              variant="inline"
              heading={t('admin.instances.load_error')}
              onRetry={() => void list.refetch()}
            />
          ) : rows.length === 0 ? (
            <p className="text-sm text-ink-2" data-testid="instance-empty">
              {t('admin.instances.empty')}
            </p>
          ) : (
            <ul className="m-0 list-none p-0" data-testid="instance-list">
              {rows.map((row) => (
                <li
                  key={row.id}
                  data-testid={`instance-row-${row.id}`}
                  className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-card-line py-3 last:border-b-0"
                >
                  <span
                    data-testid={`instance-status-${row.id}`}
                    className={[
                      'shrink-0 rounded-pill px-2 py-0.5 text-caption font-bold',
                      STATUS_TONE[row.status] ?? 'bg-inset text-ink-2',
                    ].join(' ')}
                  >
                    {t(`admin.instances.status.${row.status}`)}
                  </span>

                  <div className="min-w-32 flex-1">
                    <p className="truncate text-sm font-extrabold text-ink">{row.title_snapshot}</p>
                    <p className="text-caption text-ink-2">{whoFor(row)}</p>
                  </div>

                  {reassignFor === row.id ? (
                    <select
                      autoFocus
                      aria-label={t('admin.instances.reassignTo')}
                      data-testid={`instance-reassign-select-${row.id}`}
                      defaultValue=""
                      onChange={(e) => {
                        const memberId = e.target.value;
                        if (!memberId) return;
                        setReassignFor(null);
                        void runGuarded(async () => {
                          await reassignMut.mutateAsync({ instanceId: row.id, memberId });
                        });
                      }}
                      className="field"
                    >
                      <option value="">{t('admin.instances.reassignTo')}</option>
                      {kids.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.short_name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="flex shrink-0 gap-2">
                      {LIVE.has(row.status) ? (
                        <>
                          <Button
                            variant="secondary"
                            size={44}
                            onClick={() => setReassignFor(row.id)}
                            data-testid={`instance-reassign-${row.id}`}
                          >
                            {t('admin.instances.reassign')}
                          </Button>
                          <Button
                            variant="danger"
                            size={44}
                            onClick={() => setConfirmCancel(row)}
                            data-testid={`instance-cancel-${row.id}`}
                          >
                            {t('admin.instances.cancel')}
                          </Button>
                        </>
                      ) : null}
                      {RESTORABLE.has(row.status) ? (
                        <Button
                          variant="secondary"
                          size={44}
                          onClick={() =>
                            void runGuarded(async () => {
                              await restoreMut.mutateAsync(row.id);
                            })
                          }
                          data-testid={`instance-restore-${row.id}`}
                        >
                          {t('admin.instances.restore')}
                        </Button>
                      ) : null}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {/* Portal: kortet har backdrop-filter og blir ellers ramma for `fixed`,
          så dimmingen dekket bare «Oppgaver i dag» (#159). */}
      {confirmCancel
        ? createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${panelId}-cancel-title`}
          className="fixed inset-0 z-50 flex items-center justify-center bg-dim p-4"
          data-testid="instance-cancel-confirm"
        >
          <div className="w-full max-w-sm space-y-4 rounded-lg bg-sheet p-6 shadow-lg">
            <h2 id={`${panelId}-cancel-title`} className="text-heading-s font-extrabold text-ink">
              {t('admin.instances.cancelConfirmTitle')}
            </h2>
            <p className="text-sm text-ink-2">
              {t('admin.instances.cancelConfirmBody', { title: confirmCancel.title_snapshot })}
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => setConfirmCancel(null)}
                data-testid="instance-cancel-abort"
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                data-testid="instance-cancel-confirm-btn"
                onClick={() => {
                  const target = confirmCancel;
                  setConfirmCancel(null);
                  void runGuarded(async () => {
                    await cancelMut.mutateAsync(target.id);
                  });
                }}
              >
                {t('admin.instances.cancel')}
              </Button>
            </div>
          </div>
        </div>,
            document.body,
          )
        : null}

      <AdminReauthModal
        open={reauth.open}
        onClose={() => setReauth({ open: false, retry: null })}
        onSuccess={onReauthSuccess}
      />
    </section>
  );
}
